import React from 'react';
import { Check, DollarSign, PencilLine } from 'lucide-react';
import { DEPOSIT_MIN } from '../../lib/walletDeposit.js';

export const DEPOSIT_TIERS = [
  { amount: 10, subtitle: 'Minimum Deposit' },
  { amount: 25, subtitle: 'Recommended', default: true },
  { amount: 50, subtitle: 'High Volume Outreach' },
];

/**
 * Preset deposit tiles plus a "Custom" tile that reveals an amount input.
 * @param {Object} props
 * @param {number|'custom'} props.selected
 * @param {(v: number|'custom') => void} props.onSelect
 * @param {string} props.customAmount - Raw input text.
 * @param {(v: string) => void} props.onCustomChange
 * @param {string} props.error - Inline validation message ('' when fine).
 * @returns {JSX.Element}
 */
export default function DepositAmountPicker({ selected, onSelect, customAmount, onCustomChange, error }) {
  const tile = (isSelected) => `p-4 rounded-xl border text-left transition relative flex flex-col justify-between ${
    isSelected
      ? 'border-civix-purple bg-civix-purple/20 ring-2 ring-civix-purple/40 text-white'
      : 'border-white/10 bg-white/5 hover:border-white/20 text-white/70'
  }`;
  const selectedMark = (
    <div className="mt-3 flex items-center gap-1 text-xs text-civix-cyan font-semibold">
      <Check className="w-3.5 h-3.5" /> Selected
    </div>
  );

  return (
    <div className="space-y-3">
      <label className="block text-xs font-semibold text-white/80 uppercase tracking-wider">Initial Wallet Deposit</label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {DEPOSIT_TIERS.map((tier) => (
          <button key={tier.amount} type="button" onClick={() => onSelect(tier.amount)} className={tile(selected === tier.amount)} aria-pressed={selected === tier.amount}>
            {tier.default && (
              <span className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold bg-civix-purple text-white uppercase tracking-wider">Recommended</span>
            )}
            <div>
              <div className="text-xl font-bold font-mono text-white flex items-center">
                <DollarSign className="w-4 h-4 text-civix-cyan" />{tier.amount}.00
              </div>
              <div className="text-xs text-white/50 mt-1">{tier.subtitle}</div>
            </div>
            {selected === tier.amount && selectedMark}
          </button>
        ))}
        <button type="button" onClick={() => onSelect('custom')} className={tile(selected === 'custom')} aria-pressed={selected === 'custom'}>
          <div>
            <div className="text-xl font-bold text-white flex items-center gap-1.5"><PencilLine className="w-4 h-4 text-civix-cyan" />Custom</div>
            <div className="text-xs text-white/50 mt-1">Choose your amount</div>
          </div>
          {selected === 'custom' && selectedMark}
        </button>
      </div>

      {selected === 'custom' && (
        <div>
          <div className="relative mt-2">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 text-sm font-semibold">$</span>
            <input
              type="number"
              min={DEPOSIT_MIN}
              step="1"
              inputMode="decimal"
              placeholder="e.g. 100.00"
              value={customAmount}
              onChange={(e) => onCustomChange(e.target.value)}
              aria-label="Custom deposit amount"
              aria-invalid={!!error}
              autoFocus
              className={`w-full pl-8 pr-3 py-2.5 rounded-xl bg-white/5 border text-white font-mono focus:border-civix-purple focus:outline-hidden ${error ? 'border-rose-500/60' : 'border-white/10'}`}
            />
          </div>
          {error
            ? <p className="mt-1.5 text-xs text-rose-300" role="alert">{error}</p>
            : <p className="mt-1.5 text-[11px] text-white/40">Minimum ${DEPOSIT_MIN}.00.</p>}
        </div>
      )}
    </div>
  );
}
